import { toast } from "sonner"
import { ApiError } from "@shared/api/api-factory"
import type { ApiResponse } from "@/shared/schemas/api-schema"

/**
 * Extracts error message from various error types
 */
export function extractErrorMessage(error: unknown): string {
  // Handle ApiError instances
  if (error instanceof ApiError) {
    return error.response.message || error.message || "An error occurred"
  }
  
  // Handle Error instances
  if (error instanceof Error) {
    return error.message
  }
  
  // Handle ApiResponse objects
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const apiResponse = error as ApiResponse<unknown>
    return apiResponse.message || "An error occurred"
  }
  
  // Handle objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    const errorObj = error as { message?: string }
    return errorObj.message || "An error occurred"
  }
  
  return "An unexpected error occurred"
}

/**
 * Handles API errors and shows appropriate toast notifications
 * @param error - The error to handle
 * @param defaultMessage - Default message if error message cannot be extracted
 * @returns The extracted error message
 */
export function handleApiError(error: unknown, defaultMessage: string = "An error occurred"): string {
  const errorMessage = extractErrorMessage(error)
  const finalMessage = errorMessage || defaultMessage
  
  toast.error(finalMessage)
  
  return finalMessage
}

/**
 * Checks if an API response indicates an error
 */
export function isApiErrorResponse<T>(response: ApiResponse<T>): boolean {
  return response.statusCode !== 200
}

/**
 * Handles API response and shows error toast if needed
 * @param response - The API response to check
 * @param successMessage - Optional success message to show
 * @param errorMessage - Optional error message override
 * @returns true if response is successful, false otherwise
 */
export function handleApiResponse<T>(
  response: ApiResponse<T>,
  successMessage?: string,
  errorMessage?: string
): boolean {
  if (isApiErrorResponse(response)) {
    const message = errorMessage || response.message || "An error occurred"
    toast.error(message)
    return false
  }
  
  if (successMessage) {
    toast.success(successMessage)
  }
  
  return true
}

